import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import type { DeepKeys } from '@tanstack/react-form'
import { Plus, X } from 'lucide-react'
import z from 'zod'
import { AudioPrompt } from '../components/audio-prompt'
import { promptData } from '../config/nodes.config'
import { v2validations } from '../config/validation.config'
import type { PromptNodeData, SidebarComponentProps } from '../types'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = PromptNodeData['config']

const fields = (nodeVars: string[]): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).prompt
  return [
    {
      name: 'name',
      componentType: 'input',
      label: 'Name',
      zodValidation: validations.shape.name,
      inputProps: {
        placeholder: 'Enter name',
      },
    },
    {
      name: 'prompt',
      type: 'array',
      className: 'relative',
      zodValidation: z.array(z.any()).min(1, 'At least one prompt is required'), // A UI issue would occur if we use the zodValidation from the schema
      defaultNewValue: promptData,
      removeItemButton: <X className="w-4 h-4 absolute top-1.5 right-0 text-tertiary" />,
      addItemButton: (props) => (
        <div>
          <Button variant="ghost" className="form-button" {...props}>
            <Plus className="w-4 h-4 mr-2" />
            Add Prompt
          </Button>
        </div>
      ),
      subFields: [
        {
          name: '',
          type: 'sub-item',
          // @ts-expect-error - Deepkeys won't support this
          component: (props) => <AudioPrompt {...props} nodeVars={nodeVars} />,
          zodValidation: validations.shape.prompt.element,
          eventDataExtractor: (data) => data as Record<string, unknown>,
        },
        {
          name: 'prompt[0].delay',
          componentType: 'inputNumber',
          className: 'mt-2 mb-2',
          zodValidation: validations.shape.prompt.element.innerType().shape.delay,
          getDynamicProps: (_, i) => ({
            inputProps: {
              type: 'number',
              label: `Wait time before Prompt ${(i ?? false) ? (i || 0) + 1 : ''}`,
              endIcon: <span className="text-tertiary text-p-400 mr-7">Seconds</span>,
              min: 0,
              max: 86400,
            },
          }),
        },
        {
          name: 'prompt[0].loops',
          componentType: 'inputNumber',
          label: 'Number of times to repeat this audio',
          className: 'mt-4',
          zodValidation: validations.shape.prompt.element.innerType().shape.loops,
          inputProps: {
            type: 'number',
            endIcon: <span className="text-tertiary text-p-400 mr-7">times</span>,
            min: 0,
          },
        },
        {
          name: 'prompt[0].silence',
          componentType: 'inputNumber',
          label: 'Minimum silence',
          className: 'mt-4',
          zodValidation: validations.shape.prompt.element.innerType().shape.silence,
          // Re-evaluate disabled state when this step's delay/beep change.
          onChangeListenTo: (_state, _i, baseName) =>
            [`${baseName}.delay`, `${baseName}.beep`] as DeepKeys<FormValues>[],
          getDynamicProps: (state, i) => {
            const step = state.prompt?.[i ?? 0]
            // Mirror phlo-core add_wait: min_silence only applies when there is a
            // wait (delay > 0) and beep is off (beep forces min_silence to None).
            const disabled = !step?.delay || Boolean(step?.beep)
            return {
              inputProps: {
                type: 'number',
                endIcon: <span className="text-tertiary text-p-400 mr-7">milliseconds</span>,
                min: 0,
                disabled,
              },
            }
          },
        },
        {
          name: 'prompt[0].beep',
          componentType: 'checkbox',
          label: 'Beep',
          className: 'mt-4',
          zodValidation: validations.shape.prompt.element.innerType().shape.beep,
          // Beep is only emitted inside add_wait (phlo-core fires it only when
          // delay > 0), so disable it when there is no wait — same gate as silence.
          onChangeListenTo: (_state, _i, baseName) =>
            [`${baseName}.delay`] as DeepKeys<FormValues>[],
          getDynamicProps: (state, i) => {
            const step = state.prompt?.[i ?? 0]
            return { inputProps: { disabled: !step?.delay } }
          },
        },
      ],
    },
  ]
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const PromptSidebarComponent = (props: SidebarComponentProps<PromptNodeData>) => {
  const { nodeVars } = props
  return <GenericSidebarComponent fields={fields(nodeVars)} {...props} />
}
